import {
  fetchWithErrorHandling,
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {useQuery, useQueryClient} from '@github-ui/react-query'
import {useInvalidateOnAliveUpdate} from '@github-ui/use-invalidate-on-alive-update'
import {useCallback, useMemo} from 'react'

import {normalizeAgentMergeSession} from '../payloads/agent-merge-session'
import {claimQueryAnnouncement} from './claim-query-announcement'

export function agentMergeSessionQueryKey(apiURL: string) {
  return [PageData.agentMergeSession, apiURL] as const
}

/** Reads the `error` field Rails returns on failures without trusting the rest of the body's shape. */
function errorMessageFromResponseBody(json: unknown): {error?: string} | undefined {
  if (typeof json !== 'object' || json === null || !('error' in json)) return undefined
  const {error} = json as {error?: unknown}

  return typeof error === 'string' ? {error} : undefined
}

// Long enough to dedupe the burst of invalidations a single Alive notification can produce, short
// enough that a window-focus refetch still catches a session that ended while the tab was hidden.
const STALE_TIME = 10_000

/**
 * Refetches the active-session query when Alive reports session activity on this pull request.
 *
 * Subscribes to `pullRequestChannel` only. Dotcom publishes session start/finish there, while the
 * merge box's other ten channels (checks, deploys, reviews, refs, ...) carry unrelated events that
 * would refetch this endpoint for no reason.
 */
export function useInvalidateAgentMergeSessionOnAliveUpdate(pullRequestChannel: string | null | undefined) {
  const apiURL = usePageDataUrl(PageData.agentMergeSession)
  const queryKey = useMemo(() => agentMergeSessionQueryKey(apiURL), [apiURL])

  useInvalidateOnAliveUpdate(pullRequestChannel, queryKey)
}

/**
 * Loads the Agent Merge session currently working on this pull request.
 *
 * Updates arrive from Alive (see `useInvalidateAgentMergeSessionOnAliveUpdate`) and `refetchOnWindowFocus`
 * plus a finite `staleTime` is the backstop for a dropped broadcast; focus only refetches queries that
 * have gone stale, so the stale time must stay finite.
 */
export function useAgentMergeSessionPageData({enabled}: {enabled: boolean}) {
  const apiURL = usePageDataUrl(PageData.agentMergeSession)
  const queryClient = useQueryClient()
  const claimAnnouncement = useCallback(
    (transition: string) => claimQueryAnnouncement(queryClient, agentMergeSessionQueryKey(apiURL), transition),
    [apiURL, queryClient],
  )

  /**
   * `require-query-persister`: the active session is live state. Restoring it from IndexedDB could
   * show "Working on it now" for a session that already ended, and this line does not block the
   * main render, so there is nothing to gain from persisting it.
   *
   * `require-query-schema`: this endpoint has no generated protobuf contract. `normalizeAgentMergeSession`
   * validates the response at runtime instead, so nothing here relies on an unchecked type assertion.
   */
  // eslint-disable-next-line @github-ui/github-monorepo/require-query-persister, @github-ui/github-monorepo/require-query-schema -- See the comment above.
  const query = useQuery({
    queryKey: agentMergeSessionQueryKey(apiURL),
    enabled,
    retry: false,
    staleTime: STALE_TIME,
    refetchOnWindowFocus: true,
    queryFn: async () => {
      const response = await fetchWithErrorHandling(apiURL)
      const json: unknown = await parseJSONWithBetterErrors(response)
      throwErrorsIfBadResponse(response, errorMessageFromResponseBody(json))
      return normalizeAgentMergeSession(json)
    },
  })

  return {...query, claimAnnouncement}
}
